/**
 * Where a secret value lives, and what counts as one (§5.25).
 *
 * A project document names its secrets and never holds them. The values are
 * sealed in the account area one row per **path**, and a path says exactly which
 * field of the document it fills:
 *
 *   api/<variableId>/-          a console variable's value with no environment selected
 *   api/<variableId>/<envId>    the same variable, in one environment
 *   env/<repoId>/<KEY>          a repository's environment variable
 *
 * Ids rather than names, so renaming a variable or a repository does not strand
 * its value; the diff and the audit turn a path back into names for reading.
 *
 * Secret values are not versioned. A path holds the value last written to it,
 * and replacing one loses the old one — the audit says who replaced it, never
 * what it was.
 */
import type { WorkspaceDocument } from './document';

/** How many values one project may seal. */
export const MAX_SECRETS = 200;
/** A token, a password, a connection string — not a file. */
export const MAX_SECRET_CHARS = 4096;

const ID = /^[A-Za-z0-9_-]{1,40}$/;
const ENV_KEY = /^[^\s/=]{1,200}$/;

export type SecretRef =
  | { kind: 'api'; variableId: string; environmentId: string | null }
  | { kind: 'env'; repoId: string; key: string };

export function apiSecretPath(variableId: string, environmentId: string | null): string {
  return `api/${variableId}/${environmentId ?? '-'}`;
}

export function envSecretPath(repoId: string, key: string): string {
  return `env/${repoId}/${key}`;
}

/** A path read back, or `null` for anything this grammar does not spell. */
export function parseSecretPath(path: string): SecretRef | null {
  if (typeof path !== 'string' || path.length > 300) return null;
  const [kind, id, last, ...extra] = path.split('/');
  if (extra.length || !id || !last || !ID.test(id)) return null;
  if (kind === 'api') {
    if (last === '-') return { kind: 'api', variableId: id, environmentId: null };
    return ID.test(last) ? { kind: 'api', variableId: id, environmentId: last } : null;
  }
  if (kind === 'env') return ENV_KEY.test(last) ? { kind: 'env', repoId: id, key: last } : null;
  return null;
}

/**
 * Every path this document has a secret for — the ones a publish may write and
 * a member's install may be handed. A console variable has one for its default
 * and one per environment of its repository.
 */
export function secretPathsOf(document: WorkspaceDocument): Set<string> {
  const paths = new Set<string>();
  for (const repo of document.repos) {
    for (const env of repo.env) if (env.isSecret) paths.add(envSecretPath(repo.id, env.key));
    for (const variable of repo.api.variables) {
      if (!variable.isSecret) continue;
      paths.add(apiSecretPath(variable.id, null));
      for (const environment of repo.api.environments) paths.add(apiSecretPath(variable.id, environment.id));
    }
  }
  return paths;
}

/**
 * A path as a person reads it: `repos[api].env[DATABASE_URL]`, or
 * `repos[api].api.variables[token] (staging)`. The same spelling `diff.ts` uses,
 * so an audit row and a revision's diff name a secret the same way. A path the
 * document no longer holds is returned as written.
 */
export function describeSecretPath(document: WorkspaceDocument | null, path: string): string {
  const ref = parseSecretPath(path);
  if (!ref || !document) return path;
  if (ref.kind === 'env') {
    const repo = document.repos.find((candidate) => candidate.id === ref.repoId);
    return repo ? `repos[${repo.name}].env[${ref.key}]` : path;
  }
  for (const repo of document.repos) {
    const variable = repo.api.variables.find((candidate) => candidate.id === ref.variableId);
    if (!variable) continue;
    const environment = repo.api.environments.find((candidate) => candidate.id === ref.environmentId);
    const where = ref.environmentId === null ? '' : ` (${environment?.name ?? ref.environmentId})`;
    return `repos[${repo.name}].api.variables[${variable.key}]${where}`;
  }
  return path;
}

/** Names that are a secret whatever their value is. */
const SECRET_NAME = /(?:^|[_.-])(?:KEY|TOKEN|SECRET|PASSWORD|PASSWD|PASS|CREDENTIALS?|AUTH)$/i;

/**
 * Values that are a credential whatever they are called: the prefixes providers
 * put on their keys precisely so a scanner can find them.
 */
const SECRET_VALUE = [
  /^sk-ant-/,
  /^sk-(?:proj-)?[A-Za-z0-9_-]{20,}/,
  /^gh[pousr]_[A-Za-z0-9]{20,}/,
  /^github_pat_/,
  /^glpat-/,
  /^xox[abposr]-/,
  /^AKIA[0-9A-Z]{16}$/,
  /^AIza[0-9A-Za-z_-]{30,}/,
  /^ya29\./,
  /^npm_[A-Za-z0-9]{30,}/,
  /-----BEGIN [A-Z ]*PRIVATE KEY-----/,
  /^Bearer\s+\S{16,}/i
];

/**
 * Whether a named value looks like a credential — what an organization's
 * onboarding refuses (§5.25). The agents' own settings accept any environment
 * variable, and one of them holding `ANTHROPIC_API_KEY` would be a key in the
 * clear in a revision every member reads, kept for good. A false positive costs
 * an admin a rename; a false negative costs a key.
 */
export function looksSecret(name: string, value: unknown): boolean {
  if (SECRET_NAME.test(name.trim())) return true;
  return looksLikeCredential(value);
}

/**
 * The value half of `looksSecret` alone: a provider's key, whatever it is
 * called. What a local service's environment refuses in the clear — its
 * *names* say `PASSWORD` for a throwaway dev database as often as not, and that
 * is a value a shared document may hold; a live token never is.
 */
export function looksLikeCredential(value: unknown): boolean {
  return typeof value === 'string' && SECRET_VALUE.some((pattern) => pattern.test(value.trim()));
}
